import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DeckGL from "@deck.gl/react";
import type { Layer } from "@deck.gl/core";
import { Map as MapLibreMap, Source, Layer as MapLayer } from "react-map-gl/maplibre";
import maplibregl from "maplibre-gl";
import type { FeatureCollection, LineString, Point, Polygon, MultiPolygon } from "geojson";
import { KMITL, SOURCE_CATALOG } from "@kmitl/shared";
import type {
  AcademicSnapshot,
  AirQualityPoint,
  CampusForecast,
  CampusZoneProperties,
  DistrictBoundary,
  DistrictPoi,
  DistrictStats,
  SubdistrictBoundary,
  ExecutiveSnapshot,
  GistdaHotspotSnapshot,
  GistdaFloodSnapshot,
  FloodAreaFeed,
  GfmFloodFeed,
  FloodWatchSnapshot,
  FloodRegionalSnapshot,
  FloodGauge,
  PcdAirStation,
  IncidentFeature,
  IntelligenceItem,
  MarketSnapshot,
  PrecipNowcast,
  WeatherSnapshot,
  CctvCamera,
} from "@kmitl/shared";

import { useFeed } from "./hooks/useFeed";
import { useNewsDigest } from "./hooks/useNewsDigest";
import { useBmaStatic } from "./hooks/useBmaStatic";
import { buildTrafficSamples, type RoadProps } from "./sim/trafficSim";
import {
  aqHeatmapLayer,
  type AqHeatmapPoint,
  bmaAqStationsLayer,
  bmaParksLayer,
  neighborhoodPoisLayer,
  type NeighborhoodPoi,
  bmaPoiLayer,
  buildingsLayer,
  surroundingBuildingsLayer,
  type SurroundingBuildingProperties,
  campusBoundaryLayer,
  cctvLayer,
  devicePresenceLayer,
  districtBoundariesLayer,
  districtLabelsLayer,
  latKrabangBoundaryLayer,
  latKrabangPoiLayer,
  subdistrictZonesLayer,
  type ZoneMetric,
  gistdaFireHotspotsLayer,
  electricityPathLayer,
  waterPathLayer,
  drainagePathLayer,
  railPathLayer,
  floodWatchStationsLayer,
  waterwaysLayer,
  waterwayFlowArrowsLayer,
  type WaterwayProperties,
  type FloodAreaProperties,
  type FloodGaugeProperties,
  floodAreaLayer,
  floodGaugeLayer,
  gfmFloodExtentLayer,
  floodGaugeLabelLayer,
  esriSatelliteLayer,
  gibsLayer,
  himawariInfraredLayer,
  openTopoTerrainLayer,
  incidentLayer,
  cityReportHeatmapLayer,
  trafficHeatmapLayer,
  transitStationsLayer,
  transitLinesLayer,
  campusGatesLayer,
  roadNetworkLayer,
  campusTreesLayer,
  // AlphaEarth Foundations
  alphaEarthLandcoverLayer,
  alphaEarthChangeLayer,
  alphaEarthSurfaceLayer,
  type AlphaEarthLandcoverProps,
  type AlphaEarthChangeProps,
  type AlphaEarthSurfaceProps,
  type AqStation,
  type BuildingProperties,
  type DistrictProperties,
  type ShuttleVehicle,
  type StationProps,
  type TransitLineProps,
  type GateProps,
  type ClassifiedRoadProps,
  type TreeProps,
} from "./map/layers";
import { ALL_LAYERS, LENSES, BASE_MAPS, type LayerId, type LensId, type BaseMapId } from "./map/presets";
import { useTile3DLayer } from "./map/Tile3DLayer";
import { useGoogleTileSession } from "./hooks/useGoogleTileSession";
import { googleTileTemplate } from "./lib/googleTiles";

import { TopBar } from "./components/TopBar";
import { WaterwayLegend } from "./components/MapLegend";

import { LayerPalette } from "./components/LayerPalette";
import { CampusBrief } from "./components/CampusBrief";
import { NewsDesk } from "./components/NewsDesk";
import { SourceCatalog } from "./components/SourceCatalog";
import { AboutModal } from "./components/AboutModal";
import { PipelinePill, useAdapterHealth } from "./components/AdapterHealthPanel";
import { Manual } from "./components/Manual";
import { StandardsPanel } from "./components/StandardsPanel";
import { SheetsPanel, loadSheetsUrl } from "./components/SheetsPanel";
import { AqiBadge, type AqiTrend } from "./components/AqiBadge";
import { CctvStreamModal } from "./components/CctvStreamModal";
import { CctvStrip } from "./components/CctvStrip";
import { BuildingCard } from "./components/BuildingCard";
import { GenericFeatureCard } from "./components/GenericFeatureCard";
import { IncidentCard } from "./components/IncidentCard";
import { BuildingSearch } from "./components/BuildingSearch";
import { TrendsPanel, type TrendsSnapshot } from "./components/TrendsPanel";
import { useWorldWeather } from "./hooks/useWorldWeather";
import { DeviceCheckIn } from "./components/DeviceCheckIn";
import { NewsTicker } from "./components/NewsTicker";
import { MarketsTicker } from "./components/MarketsTicker";
import { MobileNav, type MobilePanel } from "./components/MobileNav";
import { ChatBox } from "./components/ChatBox";
import { WarnIcon } from "./components/Icons";
import { EnergyPanel } from "./components/EnergyPanel";
import { CityReportsDashboard } from "./components/CityReportsDashboard";
import { readingFor, campusSnapshot, type BuildingEnergyReading } from "./lib/energyModel";
import { ExecutiveBrief } from "./components/ExecutiveBrief";
import { PeerComparison } from "./components/PeerComparison";
import { StrategicAlerts } from "./components/StrategicAlerts";
import { ForecastSparkline } from "./components/ForecastSparkline";
import { GistdaPulse } from "./components/GistdaPulse";
import { AnalyticsBoard } from "./components/AnalyticsBoard";
import { CampusOccupancyPanel } from "./components/CampusOccupancyPanel";
import { LatKrabangPanel } from "./components/LatKrabangPanel";
import { DistrictInsightsPanel } from "./components/DistrictInsightsPanel";
import { TransitBoard } from "./components/TransitBoard";
import { ParkingPanel } from "./components/ParkingPanel";
import { CampusControlPanel } from "./components/CampusControlPanel";
import { FloodWatchPanel } from "./components/FloodWatchPanel";
import { WaterWatershedPanel } from "./components/WaterWatershedPanel";
import { FloodAreaBoard } from "./components/FloodAreaBoard";
import GfmExtentPanel from "./components/GfmExtentPanel";
import { FloodAirHeroStrip } from "./components/FloodAirHeroStrip";
import { SubstructureLegend } from "./components/SubstructureLegend";
import { useDevicePresence } from "./hooks/useDevicePresence";
import { useIsMobile } from "./hooks/useMediaQuery";
import { useOnlineStatus } from "./hooks/useOnlineStatus";
import { useTheme } from "./hooks/useTheme";

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

/** Which mobile sheet the shell shows.
 *
 *  Spelled out rather than interpolated (`mobile-panel-${mobilePanel}`) so the
 *  class names are literal tokens in the source — the dead-CSS guard detects
 *  a template-built class by scanning for its prefix, and a template nested
 *  inside another template defeats that scan. */
const MOBILE_PANEL_CLASS: Record<MobilePanel, string> = {
  map: "mobile-panel-map",
  brief: "mobile-panel-brief",
  layers: "mobile-panel-layers",
};

/**
 * Geographic lock for the KMITL Control Tower. Everything the user sees
 * must live inside the Lat Krabang district bbox — same scope as the
 * district lens — so the camera can't drift out to all Bangkok when a
 * thumb flicks the trackpad. ~1 km buffer outside the OSM admin_level=6
 * outline so neighbouring roads (Motorway No.7 to the south, ARL Lat
 * Krabang to the north, Chalong Krung corridor to the west) stay visible
 * but the wider city is out of reach.
 *
 * Format: [[west, south], [east, north]] — MapLibre's LngLatBoundsLike.
 */
const LAT_KRABANG_BOUNDS: [[number, number], [number, number]] = [
  // South edge includes Bang Sao Thong WL (~13.67) used by FLOOD WATCH.
  [100.695, 13.660],
  [100.890, 13.815],
];

/** Same numbers as plain min/max — handy for the deck.gl viewState clamp. */
const LAT_KRABANG_LNG_MIN = LAT_KRABANG_BOUNDS[0][0];
const LAT_KRABANG_LAT_MIN = LAT_KRABANG_BOUNDS[0][1];
const LAT_KRABANG_LNG_MAX = LAT_KRABANG_BOUNDS[1][0];
const LAT_KRABANG_LAT_MAX = LAT_KRABANG_BOUNDS[1][1];

/**
 * Basemap — pure dark void. No raster tiles, no street labels, no road network.
 * Earlier we pulled Esri "Dark Gray Canvas" raster basemap, but it painted
 * street centrelines + thousands of label dots over everything; combined with
 * the deck.gl overlays it made the map unreadable. A flat dark fill lets
 * the operator's eye land on waterways, buildings, and flood polygons —
 * the things they can act on — and recedes everything else to nothing.
 *
 * If a user needs to see street context they switch to the Aerial base map
 * (Esri World Imagery), which is a real satellite composite and explicitly
 * opt-in via the base-map picker.
 */
function makeBasemapVoid(): maplibregl.StyleSpecification {
  return {
    version: 8,
    sources: {},
    layers: [
      { id: "void", type: "background", paint: { "background-color": "#0a0e14" } },
    ],
  };
}

const BASEMAP_DARK = makeBasemapVoid();
// High-contrast uses the same void — maximum contrast comes from the deck.gl
// overlays, not from any raster tile underneath.
const BASEMAP_HIGH_CONTRAST = makeBasemapVoid();

// Light theme: pure warm-white ground (no raster labels) so the deck.gl
// overlays carry every visual mark. The earlier "Esri Light Gray Reference"
// raster printed thousands of tiny label dots which competed with our
// deck.gl symbols; switching to a flat fill lets every drawn line mean
// something we own.
const BASEMAP_LIGHT: maplibregl.StyleSpecification = {
  version: 8,
  sources: {},
  layers: [
    { id: "basemap", type: "background", paint: { "background-color": "#f7f5ef" } },
  ],
};

function normalizeProperties<T extends { features?: Array<{ properties?: object | null }> }>(data: T): T {
  if (data && Array.isArray(data.features)) {
    for (const f of data.features) {
      if (f.properties === null || f.properties === undefined) {
        (f as Record<string, unknown>).properties = {};
      }
    }
  }
  return data;
}

/**
 * Load and cache a GeoJSON file lazily. Pass `null` for `path` to defer the
 * fetch (used for heavy AlphaEarth files that we only want to download when
 * the user actually enables the layer).
 */
function useGeoJson<T extends { features?: Array<{ properties?: object | null }> }>(path: string | null) {
  const [data, setData] = useState<T | null>(null);
  useEffect(() => {
    if (!path) return;
    const ctrl = new AbortController();
    fetch(path, { signal: ctrl.signal })
      .then((r) => r.json())
      .then((j) => {
        setData(normalizeProperties(j as T));
      })
      .catch(() => {});
    return () => {
      ctrl.abort();
    };
  }, [path]);
  return data;
}


// ── Imagery status bar ──────────────────────────────────────────────────
// Tells the user which satellite is currently rendering and when that
// imagery was fetched. GIBS layers report the data date; Esri "World
/**
 * Latest verified Sentinel-1 observation, held at module scope.
 *
 * IMAGERY_META is a module-level table, so its `getDate` cannot close over a
 * hook. This mirrors the existing entries, which already read `Date.now()` at
 * call time rather than capturing a value at module-evaluation time.
 */
let gfmObservation: GfmFloodFeed["latest"] = null;

/**
 * Date caption for the Sentinel-1 flood-extent layer.
 *
 * The wording is deliberate. "imagery date 2026-10-09" would let a reader assume
 * the picture is live; it is not, it is the most recent PASS that saw water, and
 * Sentinel-1 may not have flown since. So the caption always carries the age,
 * and when there is no observation it says so rather than falling back to a
 * generic string.
 */
function gfmObservationLabel(): string {
  const latest = gfmObservation;
  if (!latest) return "no pass in the last 21 days saw water here";
  const d = latest.ageDays;
  const when = d === 0 ? "today" : d === 1 ? "yesterday" : `${d} days ago`;
  return `Sentinel-1 pass ${latest.observationDate} · ${when}`;
}

// Imagery" is a stitched composite and we honestly only know the session
// load time.
const IMAGERY_META: Record<string, { label: string; source: string; getDate: (sessionAt: string) => string }> = {
  "satellite-esri":              { label: "Esri World Imagery",         source: "Esri/ArcGIS Online", getDate: (s) => `tab opened ${new Date(s).toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })}` },
  "satellite-true-color":        { label: "MODIS Terra true-color",      source: "NASA GIBS",          getDate: () => `imagery date ${new Date(Date.now() - 24*60*60_000).toISOString().slice(0,10)}` },
  "satellite-viirs-truecolor":   { label: "VIIRS NOAA-20 true-color",    source: "NASA GIBS",          getDate: () => `imagery date ${new Date(Date.now() - 24*60*60_000).toISOString().slice(0,10)}` },
  "satellite-night":             { label: "VIIRS DayNightBand",          source: "NASA GIBS",          getDate: () => `imagery date ${new Date(Date.now() - 24*60*60_000).toISOString().slice(0,10)}` },
  "satellite-himawari":          { label: "Himawari-9 IR Band 13",       source: "NASA GIBS",          getDate: () => `imagery date ${new Date().toISOString().slice(0,10)} (10-min cadence)` },
  "satellite-imerg":             { label: "IMERG precipitation",         source: "NASA GIBS",          getDate: () => `imagery date ${new Date(Date.now() - 24*60*60_000).toISOString().slice(0,10)}` },
  "satellite-ndvi":              { label: "MODIS NDVI 8-day",            source: "NASA GIBS",          getDate: () => `8-day composite ending ${new Date(Date.now() - 24*60*60_000).toISOString().slice(0,10)}` },
  "satellite-lst":               { label: "MODIS Land Surface Temp",     source: "NASA GIBS",          getDate: () => `imagery date ${new Date(Date.now() - 24*60*60_000).toISOString().slice(0,10)}` },
  "satellite-aerosol":           { label: "MODIS Aerosol Optical Depth", source: "NASA GIBS",          getDate: () => `imagery date ${new Date(Date.now() - 24*60*60_000).toISOString().slice(0,10)}` },
  "satellite-no2":               { label: "OMI NO₂ tropospheric column", source: "NASA GIBS",          getDate: () => `imagery date ${new Date(Date.now() - 24*60*60_000).toISOString().slice(0,10)}` },
  "satellite-flood":             { label: "MODIS 3-day flood map",       source: "NASA GIBS",          getDate: () => `imagery date ${new Date(Date.now() - 24*60*60_000).toISOString().slice(0,10)}` },
  "gfm-flood-extent":           { label: "Observed flood extent (Sentinel-1)", source: "Copernicus GFM", getDate: () => gfmObservationLabel() },
  "satellite-terrain":           { label: "OpenTopoMap terrain",         source: "OpenTopoMap",        getDate: () => `tile composite (updates monthly)` },
  "alphaearth-landcover":        { label: "αEarth land cover",           source: "Google DeepMind",    getDate: () => `AlphaEarth Foundations · annual embedding` },
  "alphaearth-change":           { label: "αEarth change detection",     source: "Google DeepMind",    getDate: () => `AlphaEarth Foundations · cosine distance` },
  "alphaearth-surface":          { label: "αEarth surface greenness",    source: "Google DeepMind",    getDate: () => `AlphaEarth Foundations · embedding projection` },
};
function ImageryStatus({ enabledLayers, sessionStartedAt }: { enabledLayers: Set<string>; sessionStartedAt: string }) {
  const active = Object.keys(IMAGERY_META).filter((id) => enabledLayers.has(id));
  if (active.length === 0) return null;
  return (
    <div className="map-imagery mono" role="status" aria-label="Active imagery sources and fetch times">
      {active.map((id) => {
        const m = IMAGERY_META[id];
        return (
          <span key={id} className="map-imagery-row">
            <span className="map-imagery-dot" />
            <span className="map-imagery-label">{m.label}</span>
            <span className="map-imagery-meta">· {m.source} · {m.getDate(sessionStartedAt)}</span>
          </span>
        );
      })}
    </div>
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export default function App() {
  const campus = useGeoJson<FeatureCollection<Polygon | MultiPolygon, CampusZoneProperties>>(
    "/geo/kmitl-campus.geojson",
  );
  const buildings = useGeoJson<FeatureCollection<Polygon | MultiPolygon, BuildingProperties>>(
    "/geo/kmitl-buildings.geojson",
  );
  const districts = useGeoJson<FeatureCollection<Polygon | MultiPolygon, DistrictProperties>>(
    "/geo/bangkok-districts.geojson",
  );
  // Real Overture Maps footprints for every building within 1 km of campus.
  // This file was previously a 58-feature set of Chulalongkorn landmarks sitting
  // 25-29 km away, drawn as invented rectangles — and it was never actually
  // loaded by anything, which is why the surrounding area looked empty.
  const surroundingBuildings = useGeoJson<
    FeatureCollection<Polygon | MultiPolygon, SurroundingBuildingProperties>
  >("/geo/surrounding-buildings.geojson");
  const roads = useGeoJson<FeatureCollection<LineString, RoadProps>>("/geo/kmitl-roads.geojson");
  const transitStations = useGeoJson<FeatureCollection<Point, StationProps>>("/geo/kmitl-transit-stations.geojson");
  const transitLines = useGeoJson<FeatureCollection<LineString, TransitLineProps>>("/geo/kmitl-transit-lines.geojson");
  const campusGates = useGeoJson<FeatureCollection<Point, GateProps>>("/geo/kmitl-gates.geojson");
  // Campus trees — documented trees from OSM, rendered at real crown radius.
  const campusTrees = useGeoJson<FeatureCollection<Point, TreeProps>>("/geo/kmitl-trees.geojson");
  const xrayElectricity = useGeoJson<FeatureCollection>("/geo/kmitl-subsurface/electricity.geojson");
  const xrayWater = useGeoJson<FeatureCollection>("/geo/kmitl-subsurface/water.geojson");
  const xrayDrainage = useGeoJson<FeatureCollection>("/geo/kmitl-subsurface/drainage.geojson");
  const xrayRail = useGeoJson<FeatureCollection>("/geo/kmitl-subsurface/rail.geojson");
  // Surface waterway network (khlongs / canals / campus drains) — 2D water
  // layer + animated flow-direction arrows. Coordinates are ordered
  // upstream→downstream so the flow direction can be derived from line
  // endpoints.
  const waterways = useGeoJson<FeatureCollection<LineString, WaterwayProperties>>("/geo/kmitl-waterways.geojson");
  // The three AlphaEarth files (~12MB each = ~36MB) are deferred until a layer
  // that actually needs them is enabled — see the lazy-gate block below.

  // Hour + weekend for traffic simulation — auto-detected, not interactive
  const hour = new Date().getHours();
  const isWeekend = (() => { const d = new Date().getDay(); return d === 0 || d === 6; })();

  // Lens + per-layer toggles + base map
  const [lens, setLens] = useState<LensId>("operations");
  const [enabledLayers, setEnabledLayers] = useState<Set<LayerId>>(
    () => new Set(LENSES.find((l) => l.id === "operations")!.layers),
  );
  // Active metric that drives the colour of the subdistrict-zone layer.
  // Defaults to "population" — most intuitive first read.
  const [zoneMetric, setZoneMetric] = useState<ZoneMetric>("population");

  // ── Lazy-loaded heavy GeoJSON ──────────────────────────────────────────────
  // The AlphaEarth files (~12 MB each) are deferred until a layer that actually
  // needs them is enabled. Each gate is a derived boolean — flipping the layer
  // toggle re-renders App and the corresponding fetch kicks off then.
  const alphaEarthNeeded = (
    enabledLayers.has("alphaearth-landcover") ||
    enabledLayers.has("alphaearth-change") ||
    enabledLayers.has("alphaearth-surface")
  );
  const aeLandcover = useGeoJson<FeatureCollection<Polygon, AlphaEarthLandcoverProps>>(
    alphaEarthNeeded ? "/geo/alphaearth/landcover.geojson" : null,
  );
  const aeChange = useGeoJson<FeatureCollection<Polygon, AlphaEarthChangeProps>>(
    alphaEarthNeeded ? "/geo/alphaearth/change.geojson" : null,
  );
  const aeSurface = useGeoJson<FeatureCollection<Polygon, AlphaEarthSurfaceProps>>(
    alphaEarthNeeded ? "/geo/alphaearth/surface.geojson" : null,
  );
  // Base map (street/topo/aerial/night/true-color). Mutually exclusive ground
  // layer that everything else stacks on top of. Persists across lens changes
  // unless a lens explicitly declares its preferred baseMap.
  const [baseMap, setBaseMap] = useState<BaseMapId>("streets");
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [standardsOpen, setStandardsOpen] = useState(false);
  const [sheetsOpen, setSheetsOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(() => {
    try { return !window.localStorage.getItem("kmitl:about-v1"); } catch { return false; }
  });
  const [sheetsConfigured, setSheetsConfigured] = useState(() => Boolean(loadSheetsUrl()));
  // CCTV drawer — collapsed by default so the operator's first read of the
  // dashboard is the map, not 6 image tiles. Tap CCTV in the topbar to
  // expand it; tap again to collapse.
  const [cctvOpen, setCctvOpen] = useState(false);

  const { theme } = useTheme();

  // Mobile: 1-column stack with bottom segmented nav to swap panels.
  const isMobile = useIsMobile();
  const online = useOnlineStatus();
  const [mobilePanel, setMobilePanel] = useState<MobilePanel>("map");

  // Controlled map viewState — needed so building-search and click can fly the camera.
  const [viewState, setViewState] = useState({
    ...KMITL.defaultView,
    minZoom: 11,  // Bangkok metro area; regional GIBS/Himawari still visible at zoom 11
    maxZoom: 20,
    transitionDuration: 0,
  });

  /**
   * Clamp a viewState candidate inside the Lat Krabang district bbox so
   * panning/zoom can't escape the focus area. Mirrors what MapLibre does
   * natively via `maxBounds`, but deck.gl renders from viewState directly,
   * so we have to clamp here too — otherwise the layers drift off while
   * the basemap pins you back, which looks broken.
   */
  const clampToLatKrabang = useCallback(
    (lng: number, lat: number, zoom: number) => ({
      longitude: Math.max(LAT_KRABANG_LNG_MIN, Math.min(LAT_KRABANG_LNG_MAX, lng)),
      latitude:  Math.max(LAT_KRABANG_LAT_MIN, Math.min(LAT_KRABANG_LAT_MAX, lat)),
      zoom:      Math.max(11, Math.min(20, zoom)),
    }),
    [],
  );

  // Mirror of `viewState` for stable closures (click handlers, etc.) that
  // would otherwise capture viewState in their dep arrays and get re-created
  // on every pan/zoom frame — which then thrashes DeckGL's onClick prop and
  // ripples re-renders downstream.
  const viewStateRef = useRef(viewState);
  viewStateRef.current = viewState;

  // Selected building for the popup card.
  const [selectedBuilding, setSelectedBuilding] = useState<BuildingProperties | null>(null);
  const [cctvModalCamera, setCctvModalCamera] = useState<CctvCamera | null>(null);
  // [lng, lat] where the current card target was picked — drives the Street View thumbnail.
  const [selectedCoord, setSelectedCoord] = useState<[number, number] | null>(null);
  const [selectedIncidentId, setSelectedIncidentId] = useState<string | null>(null);
  const [selectedFeature, setSelectedFeature] = useState<{ layerId: string; properties: Record<string, unknown> } | null>(null);

  // Live cursor coordinate readout. Throttled to ~10Hz via a ref so we
  // don't trash every drag frame with a re-render storm — at 60Hz mouse
  // sampling that was rebuilding the deck.gl layer set on each pixel.
  const [cursorCoord, setCursorCoord] = useState<[number, number] | null>(null);
  const cursorLastCommitMs = useRef(0);

  // Page-mount timestamp — used as a stand-in for "Esri composite refresh time"
  // because Esri World Imagery is a stitched mosaic from many sources at
  // different dates; the most truthful thing we can say is "this is what
  // your browser loaded just now." GIBS layers expose a per-layer date.
  const sessionStartedAt = useMemo(() => new Date().toISOString(), []);

  // Camera helpers
  const flyTo = useCallback((longitude: number, latitude: number, zoom = 17) => {
    setViewState((prev) => {
      const clamped = clampToLatKrabang(longitude, latitude, zoom);
      return {
        ...prev,
        longitude: clamped.longitude,
        latitude: clamped.latitude,
        zoom: clamped.zoom,
        transitionDuration: 700,
      };
    });
  }, [clampToLatKrabang]);
  const handleMapClick = useCallback((info: { layer?: { id?: string } | null; object?: unknown; coordinate?: number[] }) => {
    if (info.layer?.id === "campus-buildings" && info.object) {
      const f = info.object as { properties: BuildingProperties; geometry: { coordinates: number[][][] | number[][][][] } };
      setSelectedBuilding(f.properties);
      setSelectedIncidentId(null);
      setSelectedFeature(null);
      // Read camera state from the ref instead of `viewState` directly so this
      // callback's identity stays stable across pan/zoom frames.
      const vs = viewStateRef.current;
      const [lng, lat] = info.coordinate ?? [vs.longitude, vs.latitude];
      setSelectedCoord([lng, lat]);
      flyTo(lng, lat, Math.max(vs.zoom, 17));
    } else if (info.layer?.id === "incidents-city-reports" && info.object) {
      // CR dot click → open the issue card with the full audit log thread.
      const f = info.object as IncidentFeature;
      setSelectedIncidentId(f.id);
      setSelectedBuilding(null);
      setSelectedFeature(null);
    } else if (info.layer?.id === "cctv-cameras" && info.object) {
      // CCTV dot click → open the live-stream modal.
      setCctvModalCamera(info.object as CctvCamera);
      setSelectedBuilding(null);
      setSelectedFeature(null);
    } else if (info.layer && info.object) {
      const obj = info.object as Record<string, unknown>;
      const properties = (obj.properties as Record<string, unknown>) ?? obj;
      setSelectedFeature({ layerId: info.layer.id ?? "layer", properties });
      setSelectedBuilding(null);
      setSelectedIncidentId(null);
    } else if (!info.layer) {
      setSelectedBuilding(null);
      setSelectedIncidentId(null);
      setSelectedFeature(null);
    }
  }, [flyTo]);

  const zoomBy = (delta: number) => {
    setViewState((prev) => ({
      ...prev,
      zoom: Math.max(prev.minZoom ?? 3, Math.min(prev.maxZoom ?? 20, prev.zoom + delta)),
      transitionDuration: 200,
    }));
  };

  // ── RAF-batched view-state updates ─────────────────────────────────────────
  // deck.gl fires `onViewStateChange` at the device input rate (often 120 Hz
  // on modern trackpads). Calling setViewState synchronously on each event
  // forced a React render at the same rate, which then walked the entire
  // ~80-layer memo and any React-memo children that depend on viewState.
  //
  // We instead buffer the latest event in a ref and let requestAnimationFrame
  // coalesce them into at most one setState per frame. deck.gl renders on its
  // own RAF loop anyway, so React being one frame behind is invisible — the
  // camera still tracks the cursor 1:1.
  const pendingViewStateEvt = useRef<{
    vs: Record<string, number>;
    userInteracting: boolean;
  } | null>(null);
  const viewStateRafScheduled = useRef(false);
  const flushPendingViewState = useCallback(() => {
    viewStateRafScheduled.current = false;
    const evt = pendingViewStateEvt.current;
    if (!evt) return;
    pendingViewStateEvt.current = null;
    const inProgrammatic = Date.now() < programmaticTransitionUntil.current;
    // Pin the camera inside Lat Krabang regardless of who/whatever moved it.
    const clamped = clampToLatKrabang(
      evt.vs.longitude,
      evt.vs.latitude,
      evt.vs.zoom,
    );
    setViewState((prev) => ({
      ...prev,
      longitude: clamped.longitude,
      latitude: clamped.latitude,
      // Clamp zoom: inertia can overshoot minZoom/maxZoom after finger release.
      zoom: Math.max(prev.minZoom ?? 11, Math.min(prev.maxZoom ?? 20, clamped.zoom)),
      pitch: inProgrammatic ? prev.pitch : evt.vs.pitch,
      bearing: inProgrammatic ? prev.bearing : evt.vs.bearing,
      // Always clear transitionDuration when the user is gesturing so their
      // drag follows the cursor 1:1 rather than animating toward a lingering
      // target. Outside interactions we keep whatever transition the
      // programmatic setter put there.
      transitionDuration: evt.userInteracting ? 0 : prev.transitionDuration,
    }));
  }, [clampToLatKrabang]);
  const onViewStateChangeBatched = useCallback(
    ({
      viewState: vs,
      interactionState,
    }: {
      viewState: Record<string, unknown>;
      interactionState?: {
        isDragging?: boolean;
        isPanning?: boolean;
        isZooming?: boolean;
        isRotating?: boolean;
      };
    }) => {
      const is = interactionState ?? {};
      const userInteracting = !!(
        is.isDragging || is.isPanning || is.isZooming || is.isRotating
      );
      // Any direct user input cancels the programmatic gate so the gesture
      // takes over immediately instead of waiting out the 900ms window.
      if (userInteracting && Date.now() < programmaticTransitionUntil.current) {
        programmaticTransitionUntil.current = 0;
      }
      pendingViewStateEvt.current = {
        vs: vs as Record<string, number>,
        userInteracting,
      };
      if (!viewStateRafScheduled.current) {
        viewStateRafScheduled.current = true;
        requestAnimationFrame(flushPendingViewState);
      }
    },
    [flushPendingViewState],
  );

  // Device GPS presence — drives the on-map pulse + DeviceCheckIn panel.
  const { presence, request: requestDevice, clear: clearDevice } = useDevicePresence();
  const firstFixFlown = useRef(false);
  useEffect(() => {
    if (!firstFixFlown.current && presence.lng != null && presence.lat != null && presence.insideCampus) {
      flyTo(presence.lng, presence.lat, 17);
      firstFixFlown.current = true;
    }
  }, [presence.lng, presence.lat, presence.insideCampus, flyTo]);

  // View mode cycles 2D → 3D (buildings extrude) → 3DS (substructure: buildings
  // turn translucent, utilities drop to their burial depth). Camera follows.
  type ViewMode = "2D" | "3D" | "3DS";
  const [viewMode, setViewMode] = useState<ViewMode>("2D");
  const is3D = viewMode === "3D" || viewMode === "3DS";
  const isSubstructure = viewMode === "3DS";

  // ── Energy model — synthetic per-building readings (stand-in for BEMS).
  // 5-min bucket inside readingFor() means values are stable for that
  // window, so the heatmap doesn't flicker between renders. We re-derive
  // every 5 min so anomalies move around realistically.
  const [energyBucket, setEnergyBucket] = useState(() => Math.floor(Date.now() / (5 * 60_000)));
  useEffect(() => {
    const id = setInterval(() => setEnergyBucket(Math.floor(Date.now() / (5 * 60_000))), 30_000);
    return () => clearInterval(id);
  }, []);
  const energyReadings = useMemo<BuildingEnergyReading[]>(() => {
    if (!buildings?.features) return [];
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _bucket = energyBucket;
    return buildings.features.map((f) => readingFor(f));
  }, [buildings, energyBucket]);
  const energySnapshot = useMemo(
    () => campusSnapshot(energyReadings, { treeCount: campusTrees?.features?.length ?? 0 }),
    [energyReadings, campusTrees],
  );
  const energyByBuildingId = useMemo(() => {
    const m = new Map<string, { currentKw: number; vsAvg: number; anomalyScore: number }>();
    for (const r of energyReadings) m.set(r.buildingId, { currentKw: r.currentKw, vsAvg: r.vsAvg, anomalyScore: r.anomalyScore });
    return m;
  }, [energyReadings]);
  const energyMode = enabledLayers.has("campus-energy");

  const cycleViewMode = useCallback(() => {
    setViewMode((prevMode) => {
      const next = prevMode === "2D" ? "3D" : prevMode === "3D" ? "3DS" : "2D";
      // Entering 3DS (X-RAY) auto-enables subsurface layers so the mode
      // is never an empty ghosted campus. Leaving strips them back off
      // unless the user re-toggles them from the palette.
      const xrayIds: LayerId[] = ["xray-electricity", "xray-water", "xray-drainage", "xray-rail"];
      setEnabledLayers((prev) => {
        const n = new Set(prev);
        if (next === "3DS") for (const id of xrayIds) n.add(id);
        else for (const id of xrayIds) n.delete(id);
        return n;
      });
      return next;
    });
  }, []);

  // Gate to disable onViewStateChange's bearing/pitch mirroring during our
  // own programmatic transitions (view-mode cycle). Without this, the very
  // first interpolated frame from deck.gl overwrites our target back to 0.
  const programmaticTransitionUntil = useRef(0);
  useEffect(() => {
    programmaticTransitionUntil.current = Date.now() + 900;  // 700ms anim + small buffer
    setViewState((prev) => ({
      ...prev,
      pitch: viewMode === "2D" ? 0 : viewMode === "3D" ? 50 : 62,
      bearing: viewMode === "2D" ? 0 : viewMode === "3D" ? -18 : -28,
      transitionDuration: 700,
    }));
  }, [viewMode]);

  // Keyboard shortcuts:
  //   Esc  — close the topmost overlay
  //   ?    — open the Manual (help reference)
  //   r    — hard-reload the page
  // Shortcuts ignore keystrokes while focus is in an editable element so they
  // don't fight with text inputs / chat / search.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Don't hijack typing
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      const editable =
        tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" ||
        target?.isContentEditable === true;

      if (e.key === "Escape") {
        if (manualOpen) setManualOpen(false);
        else if (catalogOpen) setCatalogOpen(false);
        else if (selectedIncidentId) setSelectedIncidentId(null);
        else if (selectedBuilding) setSelectedBuilding(null);
        return;
      }

      if (editable || e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key === "?" || (e.shiftKey && e.key === "/")) {
        e.preventDefault();
        setManualOpen(true);
      } else if (e.key === "r" || e.key === "R") {
        e.preventDefault();
        window.location.reload();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [catalogOpen, selectedBuilding, selectedIncidentId, manualOpen]);

  // Lookup table for hover tooltips — keeps DeckGL declarative.
  const tooltipForPickMemo = useCallback((info: { layer?: { id?: string } | null; object?: unknown }) => {
    const o = info.object as Record<string, unknown> | undefined;
    if (!info.layer || !o) return null;
    const id = info.layer.id;
    const p = (o as { properties?: Record<string, unknown> }).properties ?? o;
    const pick = (...keys: string[]) => {
      for (const k of keys) {
        const v = (p as Record<string, unknown>)[k];
        if (typeof v === "string" && v.trim()) return v;
        if (typeof v === "number") return String(v);
      }
      return null;
    };
    let title: string | null = null;
    let sub: string | null = null;
    switch (id) {
      case "campus-buildings":
        title = pick("nameEn", "name", "nameTh") ?? "Building";
        sub = (() => {
          const lvls = (p as { levels?: number }).levels;
          const h = (p as { height?: number }).height;
          if (h) return `${h} m tall`;
          if (lvls) return `${lvls} floors`;
          return null;
        })();
        break;
      case "campus-trees":
        title = pick("name", "species", "speciesTh", "genus") ?? "Tree";
        sub = (() => {
          const h = (p as { heightM?: number }).heightM;
          const c = (p as { crownRadiusM?: number }).crownRadiusM;
          const lt = (p as { leafType?: string }).leafType;
          const parts: string[] = [];
          if (h) parts.push(`${h} m tall`);
          if (c) parts.push(`canopy ${c} m radius`);
          if (lt) parts.push(lt);
          return parts.length ? parts.join(" · ") : "documented · OSM placeholder";
        })();
        break;
      case "alphaearth-landcover":
        title = (p as { class?: string }).class ?? "Land cover";
        sub = `AlphaEarth · 10 m · class ${(p as { classIndex?: number }).classIndex ?? "—"}`;
        break;
      case "alphaearth-change":
        title = "Surface change";
        sub = `Δ ${((p as { changeDistance?: number }).changeDistance ?? 0).toFixed(2)} · ${(p as { yearFrom?: number }).yearFrom ?? "—"}→${(p as { yearTo?: number }).yearTo ?? "—"}`;
        break;
      case "alphaearth-surface":
        title = "Surface properties";
        sub = `greenness ${((p as { greenness?: number }).greenness ?? 0).toFixed(2)} · built-up ${((p as { builtUp?: number }).builtUp ?? 0).toFixed(2)}`;
        break;
      case "incidents-itic":
      case "incidents-city-reports":
        title = pick("title") ?? "Incident";
        sub = pick("category", "severity", "status");
        break;
      case "bma-pois":
        title = pick("name") ?? "BMA POI";
        sub = pick("kind", "description");
        break;
      case "bma-aq-stations":
        title = pick("name") ?? "AQ station";
        sub = `PM2.5 ${pick("pm25") ?? "—"} µg/m³`;
        break;
      case "cctv-cameras":
        title = pick("name") ?? "CCTV";
        sub = pick("vendor");
        break;
      case "transit-stations":
        title = pick("name") ?? "Transit station";
        sub = `${pick("system") ?? ""} · ${pick("line") ?? ""}`;
        break;
      case "bangkok-districts":
        title = pick("nameEn") ?? "District";
        sub = `${pick("nameTh") ?? ""} · ${pick("code") ?? ""}`;
        break;
      default:
        return null;
    }
    return {
      html:
        `<div class="picker-tooltip"><div class="picker-title">${escapeHtml(title ?? "")}</div>` +
        (sub ? `<div class="picker-sub">${escapeHtml(sub)}</div>` : "") +
        `</div>`,
      style: {
        background: "transparent",
        border: "none",
        padding: "0",
      },
    };
  }, []);

  const onLensChange = useCallback((id: LensId) => {
    setLens(id);
    const next = LENSES.find((l) => l.id === id);
    if (next) {
      setEnabledLayers(new Set(next.layers));
      // A lens may carry its preferred base map (EXEC/ENV/VIB → aerial).
      // Picking the lens snaps both the layer set AND the ground style.
      if (next.baseMap) setBaseMap(next.baseMap);
    }
  }, []);

  // ── Expo attract loop — booth operation for the KMITL expo ────────────────
  // ?expo=1 (or the topbar EXPO button) auto-cycles through every lens on a
  // 45 s dwell. Any pointer/key interaction pauses the loop; it resumes after
  // 60 s idle so a visitor can explore without fighting the rotation.
  const [expoMode, setExpoMode] = useState<boolean>(() =>
    typeof window !== "undefined" && new URLSearchParams(window.location.search).has("expo"),
  );
  const expoLastTouch = useRef(0);
  const lensRef = useRef(lens);
  lensRef.current = lens;
  useEffect(() => {
    if (!expoMode) return;
    const EXPO_DWELL_MS = 45_000;
    const IDLE_RESUME_MS = 60_000;
    // The click that enabled the loop must not count as visitor activity,
    // or the first advance would stall for a full idle window.
    expoLastTouch.current = 0;
    const markTouch = () => { expoLastTouch.current = Date.now(); };
    window.addEventListener("pointerdown", markTouch);
    window.addEventListener("keydown", markTouch);
    const tick = window.setInterval(() => {
      if (Date.now() - expoLastTouch.current < IDLE_RESUME_MS) return; // visitor active — hold
      const idx = LENSES.findIndex((l) => l.id === lensRef.current);
      onLensChange(LENSES[(idx + 1) % LENSES.length].id);
    }, EXPO_DWELL_MS);
    return () => {
      window.removeEventListener("pointerdown", markTouch);
      window.removeEventListener("keydown", markTouch);
      window.clearInterval(tick);
    };
  }, [expoMode, onLensChange]);
  // Regional GIBS / Himawari satellites are 250 m–1 km globals. They render
  // at maxZoom 6–9; at campus zoom (~17) they would either smear or stay
  // invisible. When the user toggles one ON while zoomed in, pull the camera
  // out so the imagery actually shows up.
  const REGIONAL_SATELLITES = useMemo(() => new Set<LayerId>([
    "satellite-true-color",
    "satellite-viirs-truecolor",
    "satellite-night",
    "satellite-himawari",
    "satellite-imerg",
    "satellite-ndvi",
    "satellite-lst",
    "satellite-aerosol",
    "satellite-no2",
    "satellite-flood",
  ]), []);
  const onToggleLayer = useCallback((id: LayerId) => {
    setEnabledLayers((prev) => {
      const next = new Set(prev);
      const turningOn = !next.has(id);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      if (turningOn && REGIONAL_SATELLITES.has(id)) {
        setViewState((v) => v.zoom > 10 ? { ...v, zoom: 10, transitionDuration: 900 } : v);
      }
      return next;
    });
  }, [REGIONAL_SATELLITES]);

  const onClearAllLayers = useCallback(() => {
    setEnabledLayers(new Set());
  }, []);

  // Feeds
  const cityReports = useFeed<IncidentFeature>(`${API_BASE}/api/incidents/city-reports`, 5 * 60_000);
  const iticEvents = useFeed<IncidentFeature>(`${API_BASE}/api/incidents/itic`, 3 * 60_000);
  const news = useFeed<IntelligenceItem>(`${API_BASE}/api/news`, 3 * 60_000);
  const newsDigest = useNewsDigest(API_BASE);
  const weather = useFeed<WeatherSnapshot>(`${API_BASE}/api/weather`, 30 * 60_000);
  const airQuality = useFeed<AirQualityPoint>(`${API_BASE}/api/air-quality`, 15 * 60_000);
  // Multi-station feed for the IQAir-style heatmap. Combines AQICN (when
  // AQICN_TOKEN is set) with the Open-Meteo KMITL center anchor. Always
  // returns at least one point so the heatmap never disappears.
  const aqStations = useFeed<AqHeatmapPoint>(`${API_BASE}/api/air-quality/stations`, 15 * 60_000);
  // OSM-derived neighborhood POIs (schools, religious, hotels, retail, banks, etc.).
  // Cached 12h server-side; we poll once an hour to catch refreshes after restarts.
  const neighborhoodPois = useFeed<NeighborhoodPoi>(`${API_BASE}/api/neighborhood-pois`, 60 * 60_000);
  const lkBoundary = useFeed<DistrictBoundary>(`${API_BASE}/api/district/lat-krabang/boundary`, 24 * 60 * 60_000);
  const lkPois = useFeed<DistrictPoi>(`${API_BASE}/api/district/lat-krabang/pois`, 24 * 60 * 60_000);
  const lkSubdistricts = useFeed<SubdistrictBoundary>(`${API_BASE}/api/district/lat-krabang/subdistricts`, 7 * 24 * 60 * 60_000);
  const lkStats = useFeed<DistrictStats>(`${API_BASE}/api/district/lat-krabang/stats`, 6 * 60 * 60_000);

  // Subdistrict zones — join OSM admin_level=7 polygons with data.go.th
  // stats by Thai subdistrict name. Falls back to zeros when the stats
  // payload hasn't loaded yet (or is in scenario tier) so the polygons
  // still render with a neutral fill instead of disappearing. Non-population
  // metrics (business / risk / students) don't all carry the subdistrict
  // name in data.go.th, so we distribute them by population share as a
  // fair, honest proxy — labels it explicitly in the legend.
  const lkZones = useMemo(() => {
    if (!lkSubdistricts.data.length) return [];
    const st = lkStats.data[0];
    const byName = new Map<string, DistrictStats["population"]["bySubdistrict"][number]>();
    if (st) for (const s of st.population.bySubdistrict) byName.set(s.subdistrict, s);
    const bizTotal = st?.businesses.total ?? 0;
    const riskTotal = st?.risks.total ?? 0;
    const studentTotal = st?.schools.totalStudents ?? 0;
    const popTotal = st?.population.total ?? 1;

    return lkSubdistricts.data.map((sub) => {
      const pop = byName.get(sub.name);
      const population = pop?.total ?? 0;
      const popShare = popTotal > 0 ? population / popTotal : 0;
      const business = Math.round(bizTotal * popShare);
      const risk = Math.round(riskTotal * popShare);
      const students = Math.round(studentTotal * popShare);
      const value =
        zoneMetric === "population" ? population :
        zoneMetric === "business"   ? business   :
        zoneMetric === "risk"       ? risk       :
        students;
      return {
        relationId: sub.relationId,
        name: sub.name,
        nameEn: sub.nameEn,
        outerRing: sub.outerRing,
        population,
        business,
        risk,
        students,
        value,
        metric: zoneMetric,
      };
    });
  }, [lkSubdistricts.data, lkStats.data, zoneMetric]);
  const cctv = useFeed<CctvCamera>(`${API_BASE}/api/cctv/longdo`, 10 * 60_000);
  const shuttle = useFeed<ShuttleVehicle>(`${API_BASE}/api/transit/kmitl-transit`, 30_000);
  const aqiTrend = useFeed<AqiTrend>(`${API_BASE}/api/air-quality/trend`, 15 * 60_000);
  const trends = useFeed<TrendsSnapshot>(`${API_BASE}/api/trends`, 15 * 60_000);
  const executive = useFeed<ExecutiveSnapshot>(`${API_BASE}/api/executive`, 15 * 60_000);
  const markets = useFeed<MarketSnapshot>(`${API_BASE}/api/markets`, 10 * 60_000);
  const precip = useFeed<PrecipNowcast>(`${API_BASE}/api/precip-nowcast`, 5 * 60_000);
  const academic = useFeed<AcademicSnapshot>(`${API_BASE}/api/academic-calendar`, 30 * 60_000);
  const campusForecast = useFeed<CampusForecast>(`${API_BASE}/api/forecast`, 30 * 60_000);
  // GISTDA fire + flood — 30 min TTL; both return scenario/empty until VIIRS tier key is active
  const gistdaFire = useFeed<GistdaHotspotSnapshot>(`${API_BASE}/api/gistda/fire`, 30 * 60_000);
  const gistdaFlood = useFeed<GistdaFloodSnapshot>(`${API_BASE}/api/gistda/flood`, 30 * 60_000);
  const floodWatch = useFeed<FloodWatchSnapshot>(`${API_BASE}/api/flood-watch`, 10 * 60_000);
  const pcdAir = useFeed<PcdAirStation>(`${API_BASE}/api/district/air-stations`, 10 * 60_000);
  const floodRegional = useFeed<FloodRegionalSnapshot>(`${API_BASE}/api/district/flood-regional`, 10 * 60_000);
  // Live HII gauge network within 30 km of campus. Refreshed on the same
  // 10-min cadence as the HII feed itself.
  const floodGaugeFeed = useFeed<FloodGauge>(`${API_BASE}/api/flood/gauges?radiusKm=30`, 10 * 60_000);

  // Per-area flood intelligence: FloodDash's scrape of Thai flood coverage,
  // matched to real named places and joined against HII sensor evidence.
  // Distinct from `floodGaugeFeed`, which is instrumentation only. Polls
  // faster than the gauges because reporting moves during an event.
  const floodAreaFeed = useFeed<FloodAreaFeed>(`${API_BASE}/api/flood/areas`, 5 * 60_000);
  // Satellite cadence is ~6 days; the adapter only reports a date it verified
  // saw water, so this can be polled slowly and never flickers to empty.
  const gfmFlood = useFeed<GfmFloodFeed>(`${API_BASE}/api/flood/gfm`, 30 * 60_000);
  gfmObservation = gfmFlood.data[0]?.latest ?? null;

  const floodAreas = useMemo<FloodAreaProperties[]>(
    () =>
      (floodAreaFeed.data[0]?.areas ?? []).map((a) => ({
        key: a.key,
        areaTh: a.areaTh,
        areaEn: a.areaEn,
        provinceEn: a.provinceEn,
        tier: a.tier,
        score: a.score,
        band: a.band,
        severity: a.severity,
        sourceCount: a.sourceCount,
        distanceKm: a.distanceKm,
        lat: a.lat,
        lng: a.lng,
      })),
    [floodAreaFeed.data],
  );
  const floodGauges = useMemo<FloodGaugeProperties[]>(
    () =>
      floodGaugeFeed.data.map((g) => ({
        key: g.key,
        nameEn: g.nameEn,
        nameTh: g.nameTh,
        provinceEn: g.provinceEn,
        lat: g.lat,
        lng: g.lng,
        distanceKm: g.distanceKm,
        situationLevel: g.situationLevel,
        storagePct: g.storagePct,
        wlMsl: g.wlMsl,
        marginToBankM: g.marginToBankM,
        band: g.band,
        bandTh: g.bandTh,
        dataConfidence: g.dataConfidence,
        ageMin: g.ageMin,
        observedAt: g.observedAt,
      })),
    [floodGaugeFeed.data],
  );
  // Merge Traffy flooding tickets + GISTDA extent count into the watch card
  // so the left rail corroborates HII gauges with citizen + satellite signals.
  const floodWatchMerged = useMemo((): FloodWatchSnapshot | null => {
    const base = floodWatch.data[0];
    if (!base) return null;
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    const floodingReports24h = cityReports.data.filter((r) => {
      if (r.category !== "flooding" && r.category !== "drainage") return false;
      const t = new Date(r.reportedAt).getTime();
      return Number.isFinite(t) && t >= cutoff;
    }).length;
    const extents = gistdaFlood.data[0]?.extents?.length ?? 0;
    const extra =
      floodingReports24h > 0 || extents > 0
        ? ` · ${floodingReports24h} Traffy flood/drain tickets (24h)${extents > 0 ? ` · ${extents} GISTDA extents` : ""}`
        : "";
    return {
      ...base,
      floodingReports24h,
      summaryEn: base.summaryEn + extra,
    };
  }, [floodWatch.data, cityReports.data, gistdaFlood.data]);
  const adapterHealth = useAdapterHealth(API_BASE, 30_000);
  const worldWeather = useWorldWeather();
  const bangkokWeather = useMemo(() => {
    const city = worldWeather.find((c) => c.city.id === "bkk");
    if (!city) return null;
    const { city: _city, fetchedAt: _fetchedAt, ...rest } = city;
    return rest;
  }, [worldWeather]);

  const bangkokPulse = useMemo(() => {
    const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
    const openReports = cityReports.data.filter((r) => r.status !== "resolved").length;
    const news24h = news.data.filter((n) => {
      const t = new Date(n.publishedAt).getTime();
      return !Number.isNaN(t) && t >= oneDayAgo;
    }).length;
    return {
      iticEvents: iticEvents.data.length,
      openReports,
      news24h,
      shuttleLive: shuttle.data.length,
    };
  }, [iticEvents.data, cityReports.data, news.data, shuttle.data]);

  // BMA POIs are bundled static GeoJSON (Workers TLS can't reach bmagis.bangkok.go.th
  // from local workerd; production CF edge may, but static is robust either way).
  const bma = useBmaStatic();
  const bmaAqStationList: AqStation[] = useMemo(() => {
    if (!bma) return [];
    return bma.aqStations.features.map((f) => ({
      id: f.properties.AIRSTATION_ID ?? "aq",
      name: f.properties.NAME_T ?? "AQ station",
      address: f.properties.ADDRESS ?? "",
      pm25: f.properties.PM25 ?? null,
      pm10: f.properties.PM10 ?? null,
      lng: (f.geometry.coordinates as [number, number])[0],
      lat: (f.geometry.coordinates as [number, number])[1],
    }));
  }, [bma]);

  // Traffic samples — recomputed when hour or roads change
  const trafficSamples = useMemo(() => {
    if (!roads) return [];
    return buildTrafficSamples(roads, hour, { isWeekend });
  }, [roads, hour, isWeekend]);

  // ── Google Maps Platform client layers ──────────────────────────────────────
  // Photorealistic 3D tiles via deck.gl (key stays referrer/quota-restricted).
  const tile3d = useTile3DLayer({
    visible: enabledLayers.has("google-3d-tiles"),
    source: "google",
  });
  // 2D Map Tiles sessions — minted lazily the first time each layer is enabled.
  const googleSatSession = useGoogleTileSession("satellite", enabledLayers.has("google-satellite"));
  const googleTrafficSession = useGoogleTileSession("traffic", enabledLayers.has("google-traffic"));

  const layers = useMemo<Layer[]>(() => {
    const out: Layer[] = [];
    // Base map first — renders beneath all vector data + atmospheric overlays.
    // "streets" leaves the carto basemap exposed (no deck.gl tile layer).
    if (baseMap === "aerial") {
      out.push(esriSatelliteLayer(1.0) as Layer);
    } else if (baseMap === "topo") {
      // OpenTopoMap max zoom is 17 — always render when the user picks Topo.
      out.push(openTopoTerrainLayer(0.95) as Layer);
    } else if (baseMap === "night") {
      // Pass undefined zoom so the layer is always visible (the zoom<10 guard
      // is for overlay mode; here it is the base map).
      out.push(gibsLayer("VIIRS_SNPP_DayNightBand_ENCC", undefined, 1.0) as Layer);
    } else if (baseMap === "true-color") {
      out.push(gibsLayer("VIIRS_NOAA20_CorrectedReflectance_TrueColor", undefined, 0.95) as Layer);
    }
    // Atmospheric / data overlays — these are additive layers, not base maps.
    // Pre-existing logic for regional satellites stays; cropped to overlays only.
    if (enabledLayers.has("satellite-himawari"))
      out.push(himawariInfraredLayer(0.55) as Layer);
    if (enabledLayers.has("satellite-imerg"))
      out.push(gibsLayer("IMERG_Precipitation_Rate", undefined, 0.7) as Layer);
    if (enabledLayers.has("satellite-ndvi"))
      out.push(gibsLayer("MODIS_Terra_NDVI_8Day", undefined, 0.6) as Layer);
    if (enabledLayers.has("satellite-lst"))
      out.push(gibsLayer("MODIS_Terra_Land_Surface_Temp_Day", undefined, 0.65) as Layer);
    if (enabledLayers.has("satellite-aerosol"))
      out.push(gibsLayer("MODIS_Combined_Value_Added_AOD", undefined, 0.7) as Layer);
    if (enabledLayers.has("satellite-no2"))
      out.push(gibsLayer("OMI_Nitrogen_Dioxide_Tropo_Column", undefined, 0.7) as Layer);
    if (enabledLayers.has("satellite-flood"))
      out.push(gibsLayer("MODIS_Combined_Flood_3-Day", undefined, 0.7) as Layer);
    // Observed flood extent on the ground. Only rendered for a date the API has
    // confirmed saw water — a blank raster would read as "no flood", which is a
    // different and much stronger claim than "the satellite saw nothing".
    if (enabledLayers.has("gfm-flood-extent")) {
      const gfmLayer = gfmFloodExtentLayer(gfmFlood.data[0]?.latest?.observationDate ?? null, 0.85);
      if (gfmLayer) out.push(gfmLayer as Layer);
    }
    // AlphaEarth Foundations — AI-derived campus intelligence layers
    if (enabledLayers.has("alphaearth-landcover") && aeLandcover)
      out.push(alphaEarthLandcoverLayer(aeLandcover, viewState.zoom) as Layer);
    if (enabledLayers.has("alphaearth-change") && aeChange)
      out.push(alphaEarthChangeLayer(aeChange, viewState.zoom) as Layer);
    if (enabledLayers.has("alphaearth-surface") && aeSurface)
      out.push(alphaEarthSurfaceLayer(aeSurface, viewState.zoom) as Layer);
    if (enabledLayers.has("bma-parks") && bma?.parks) out.push(bmaParksLayer(bma.parks) as Layer);
    if (enabledLayers.has("campus-trees") && campusTrees)
      out.push(campusTreesLayer(campusTrees, { extruded: is3D }) as Layer);
    const useGoogle3D = enabledLayers.has("google-3d-tiles") && import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (tile3d.layer) out.push(tile3d.layer as Layer);

    if (enabledLayers.has("campus-boundary") && campus) out.push(campusBoundaryLayer(campus) as Layer);
    if (enabledLayers.has("campus-buildings") && buildings && !useGoogle3D)
      out.push(buildingsLayer(buildings, {
        extruded: is3D,
        ghosted: isSubstructure,
        energyByBuildingId: energyMode ? energyByBuildingId : undefined,
      }) as Layer);
    // Surrounding urban fabric — 1,790 real footprints within 1 km. Drawn
    // under the campus buildings so the named campus structures stay on top.
    if (enabledLayers.has("surrounding-buildings") && surroundingBuildings?.features?.length)
      out.push(surroundingBuildingsLayer(surroundingBuildings, {
        ghosted: isSubstructure,
      }) as Layer);
    if (enabledLayers.has("road-network") && roads)
      out.push(roadNetworkLayer(roads as unknown as FeatureCollection<LineString, ClassifiedRoadProps>) as Layer);
    if (enabledLayers.has("transit-lines") && transitLines)
      out.push(transitLinesLayer(transitLines) as Layer);
    if (enabledLayers.has("campus-gates") && campusGates)
      out.push(campusGatesLayer(campusGates) as Layer);
    if (enabledLayers.has("bangkok-districts") && districts) {
      out.push(districtBoundariesLayer(districts) as Layer);
      out.push(districtLabelsLayer(districts) as Layer);
    }
    // GISTDA VIIRS fire hotspots — renders when VIIRS tier key is active; graceful
    // no-op when the feed returns empty (scenario tier, current state).
    if (enabledLayers.has("gistda-fire-hotspots")) {
      const hotspots = gistdaFire.data[0]?.hotspots ?? [];
      if (hotspots.length > 0) out.push(gistdaFireHotspotsLayer(hotspots) as Layer);
    }
    if (enabledLayers.has("bma-pois") && bma && bma.pois.length > 0) out.push(bmaPoiLayer(bma.pois) as Layer);
    // IQAir-style PM2.5 heatmap renders BEFORE the station dots so the dots
    // sit on top of the gradient.
    if (enabledLayers.has("aq-heatmap") && aqStations.data.length > 0)
      out.push(aqHeatmapLayer(aqStations.data) as Layer);
    if (enabledLayers.has("bma-aq-stations") && bmaAqStationList.length > 0) out.push(bmaAqStationsLayer(bmaAqStationList) as Layer);
    // Neighborhood POIs — one deck.gl layer per enabled category so toggling is
    // per-category in the palette. Render below incidents/CCTV but above the
    // base layers so they're easy to spot.
    if (neighborhoodPois.data.length > 0) {
      const POI_BY_LAYER: Record<string, NeighborhoodPoi["category"]> = {
        "poi-school":    "school",
        "poi-religious": "religious",
        "poi-hotel":     "hotel",
        "poi-pharmacy":  "pharmacy",
        "poi-cultural":  "cultural",
        "poi-bus-stop":  "bus_stop",
        "poi-pier":      "pier",
        "poi-embassy":   "embassy",
        "poi-ev":        "ev",
        "poi-retail":    "retail",
        "poi-bank":      "bank",
      };
      for (const [layerId, category] of Object.entries(POI_BY_LAYER)) {
        if (enabledLayers.has(layerId as LayerId)) {
          out.push(neighborhoodPoisLayer(category, neighborhoodPois.data) as Layer);
        }
      }
    }
    // Lat Krabang district — boundary polygon + dot POIs + colour-coded
    // subdistrict zones. All toggleable in the LATKRABANG lens. Draw
    // order: zones → boundary → POIs, so dots stay readable on top of
    // the metric fills.
    if (enabledLayers.has("lat-krabang-zones") && lkZones.length > 0) {
      const zones = subdistrictZonesLayer(lkZones, zoneMetric);
      if (zones) out.push(zones as Layer);
    }
    if (enabledLayers.has("lat-krabang-boundary") && lkBoundary.data.length > 0) {
      const lk = latKrabangBoundaryLayer(lkBoundary.data[0]);
      if (lk) out.push(lk as Layer);
    }
    if (enabledLayers.has("lat-krabang-pois") && lkPois.data.length > 0) {
      out.push(latKrabangPoiLayer(lkPois.data) as Layer);
    }
    if (enabledLayers.has("traffic-heatmap") && trafficSamples.length > 0) out.push(trafficHeatmapLayer(trafficSamples) as Layer);
    if (enabledLayers.has("transit-stations") && transitStations) out.push(transitStationsLayer(transitStations) as Layer);
    if (enabledLayers.has("cctv-cameras")) out.push(cctvLayer(cctv.data) as Layer);
    if (enabledLayers.has("incidents-city-heatmap")) out.push(cityReportHeatmapLayer(cityReports.data) as Layer);
    if (enabledLayers.has("incidents-city-reports")) out.push(incidentLayer("incidents-city-reports", cityReports.data) as Layer);
    if (enabledLayers.has("incidents-itic")) out.push(incidentLayer("incidents-itic", iticEvents.data) as Layer);

    // HII ThaiWater flood / rain gauges (live watch)
    if (enabledLayers.has("flood-watch-stations")) {
      const snap = floodWatch.data[0];
      if (snap && (snap.waterStations.length > 0 || snap.rainStations.length > 0)) {
        out.push(...(floodWatchStationsLayer(snap.waterStations, snap.rainStations) as Layer[]));
      }
    }

    // Surface waterway network (khlongs / canals / campus drains) — 2D.
    // Renders below the flood stations so the dots sit on top of the lines.
    if (enabledLayers.has("waterways") && waterways) {
      out.push(...(waterwaysLayer(waterways) as Layer[]));
    }
    // Animated flow-direction arrows. Same data, but IconLayer with a
    // canvas-rendered triangle that rotates by the line's bearing.
    if (enabledLayers.has("waterways-flow") && waterways) {
      out.push(waterwayFlowArrowsLayer(waterways) as Layer);
    }
    // Live HII gauge network from /api/flood/gauges (FloodDash → thaiwater).
    // Dots + labels on top of the waterway lines so the operator can see both
    // the channel network and what it is doing right now.
    if (enabledLayers.has("flood-gauges") && floodGauges.length > 0) {
      out.push(floodGaugeLayer(floodGauges) as Layer);
      out.push(floodGaugeLabelLayer(floodGauges) as Layer);
    }
    // Per-area flood reporting from /api/flood/areas. Drawn UNDER the gauges:
    // a named place is the coarse fact, a gauge is the precise one, so the
    // precise one sits on top.
    if (enabledLayers.has("flood-areas") && floodAreas.length > 0) {
      out.push(...(floodAreaLayer(floodAreas) as Layer[]));
    }

    // X-RAY subsurface — KMITL-local corridors (auto-on in 3DS view mode)
    if (enabledLayers.has("xray-drainage") && xrayDrainage)
      out.push(...(drainagePathLayer(xrayDrainage) as Layer[]));
    if (enabledLayers.has("xray-water") && xrayWater)
      out.push(...(waterPathLayer(xrayWater) as Layer[]));
    if (enabledLayers.has("xray-electricity") && xrayElectricity)
      out.push(...(electricityPathLayer(xrayElectricity) as Layer[]));
    if (enabledLayers.has("xray-rail") && xrayRail)
      out.push(...(railPathLayer(xrayRail) as Layer[]));

    return out;
  }, [
    enabledLayers, baseMap, is3D, isSubstructure, campus, buildings,
    districts, trafficSamples,
    transitStations, transitLines, campusGates, roads,
    cctv.data, cityReports.data,
    iticEvents.data, bma, bmaAqStationList, aqStations.data, neighborhoodPois.data,
    gistdaFire.data, floodWatch.data,
    // These three are read in the body above and MUST be listed here. Each is a
    // separate SWR feed on its own refresh interval, so a rebuild triggered by
    // `floodWatch` does not mean the gauge, area or GFM feeds have landed. Left
    // out, the layer memo never recomputes when their data arrives and the
    // layers silently never render — the build is complete but has no edge into
    // the render path.
    floodGauges, floodAreas, gfmFlood.data, surroundingBuildings,
    xrayElectricity, xrayWater, xrayDrainage, xrayRail,
    energyMode, energyByBuildingId,
    campusTrees,
    tile3d.layer,
  ]);

  // Device presence pulse runs in its own tiny memo so GPS updates (which can
  // tick at ~1 Hz when the watcher is active) do not invalidate the main
  // layer memo and force ~80 layers to be reconstructed on each fix.
  const presenceLayers = useMemo<Layer[]>(() => {
    if (presence.lng == null || presence.lat == null) return [];
    const [accDisk, dot] = devicePresenceLayer(presence.lng, presence.lat, presence.accuracyM);
    return [accDisk as Layer, dot as Layer];
  }, [presence.lng, presence.lat, presence.accuracyM]);

  // Final layer array passed to DeckGL. Concatenation is cheap; the inner
  // memos prevent any deep work from happening here.
  const allLayers = useMemo<Layer[]>(
    () => (presenceLayers.length > 0 ? [...layers, ...presenceLayers] : layers),
    [layers, presenceLayers],
  );

  const feedHealth = useMemo(() => [
    { label: "NEWS", tier: news.fallbackTier, ageMinutes: news.ageMinutes },
    { label: "CR", tier: cityReports.fallbackTier, ageMinutes: cityReports.ageMinutes },
    { label: "iTIC", tier: iticEvents.fallbackTier, ageMinutes: iticEvents.ageMinutes },
    { label: "BMA", tier: (bma ? "cache" : "loading") as "cache" | "loading", ageMinutes: 0 },
    { label: "CCTV", tier: cctv.fallbackTier, ageMinutes: cctv.ageMinutes },
    { label: "BUS", tier: shuttle.fallbackTier, ageMinutes: shuttle.ageMinutes },
    { label: "AQ", tier: aqiTrend.fallbackTier, ageMinutes: aqiTrend.ageMinutes },
    { label: "WX", tier: weather.fallbackTier, ageMinutes: weather.ageMinutes },
    { label: "EX", tier: executive.fallbackTier, ageMinutes: executive.ageMinutes },
    { label: "MK", tier: markets.fallbackTier, ageMinutes: markets.ageMinutes },
  ], [news.fallbackTier, news.ageMinutes, cityReports.fallbackTier, cityReports.ageMinutes, iticEvents.fallbackTier, iticEvents.ageMinutes, bma, cctv.fallbackTier, cctv.ageMinutes, shuttle.fallbackTier, shuttle.ageMinutes, aqiTrend.fallbackTier, aqiTrend.ageMinutes, weather.fallbackTier, weather.ageMinutes, executive.fallbackTier, executive.ageMinutes, markets.fallbackTier, markets.ageMinutes]);

  // Stable panel callbacks. App re-renders on every RAF frame while the camera
  // moves (viewState drives the compass, coord readout, zoom). Passing inline
  // arrows here would give React.memo'd panels a fresh prop identity each frame
  // and defeat the memo — keep these referentially stable so panels skip the
  // re-render during pan/zoom.
  const handleCityFlyTo = useCallback((lng: number, lat: number) => flyTo(lng, lat, 18), [flyTo]);
  const handleSelectIncident = useCallback((id: string) => setSelectedIncidentId(id), []);
  const handleCloseBuilding = useCallback(() => setSelectedBuilding(null), []);
  const handleCloseFeature = useCallback(() => setSelectedFeature(null), []);
  const handleCloseIncident = useCallback(() => setSelectedIncidentId(null), []);
  const handleBuildingSearchSelect = useCallback(
    (centroid: [number, number], props: BuildingProperties) => {
      flyTo(centroid[0], centroid[1], 17.5);
      setSelectedCoord([centroid[0], centroid[1]]);
      // Open the card after the 700 ms camera transition so the card's
      // mount render doesn't compete with the animation frames.
      setTimeout(() => setSelectedBuilding(props), 720);
    },
    [flyTo],
  );
  const handleEnergySelectBuilding = useCallback(
    (b: BuildingEnergyReading) => {
      const f = buildings?.features.find((x) => (x.properties.id ?? "") === b.buildingId);
      if (f) {
        const c =
          f.geometry.type === "Polygon"
            ? f.geometry.coordinates[0][0]
            : f.geometry.coordinates[0][0][0];
        if (c) flyTo(c[0], c[1], 18);
      }
    },
    [buildings, flyTo],
  );
  return (
    <div
      className={`shell ${cctvOpen ? "cctv-open" : ""} ${isMobile ? `mobile ${MOBILE_PANEL_CLASS[mobilePanel]}` : ""}`}
      data-mobile={isMobile ? "true" : "false"}
    >
      {!online && (
        <div className="offline-banner mono" role="alert" aria-live="assertive">
          <WarnIcon size={13} /> OFFLINE — feeds are stale until the connection returns
        </div>
      )}
      {/* ── Top bar ── */}
      <TopBar
        feeds={feedHealth}
        onOpenCatalog={() => setCatalogOpen(true)}
        catalogCount={SOURCE_CATALOG.length}
        viewMode={viewMode}
        onCycleViewMode={cycleViewMode}
        onOpenManual={() => setManualOpen(true)}
        onOpenSheets={() => setSheetsOpen(true)}
        onOpenStandards={() => setStandardsOpen(true)}
        onOpenAnalytics={() => setAnalyticsOpen(true)}
        sheetsConfigured={sheetsConfigured}
        academic={academic.data[0] ?? null}
        adapterHealth={adapterHealth}
        lens={LENSES.find((l) => l.id === lens) ?? null}
        expoMode={expoMode}
        onToggleExpo={() => setExpoMode((v) => !v)}
        cctvOpen={cctvOpen}
        onToggleCctv={() => setCctvOpen((v) => !v)}
      />

      {/* ── CCTV drawer: collapsed by default — operator opens it from the
         topbar CCTV chip when they want to scan the regional approach roads.
         When closed it costs zero vertical chrome; when open it slides down
         as a 96-px band so the map still owns the bulk of the screen. ── */}
      <CctvStrip
        onOpenCamera={(c) => {
          setCctvModalCamera(c);
          flyTo(c.lng, c.lat, 14);
        }}
        maxTiles={6}
        refreshMs={5 * 60_000}
        open={cctvOpen}
      />

      {/* ── Flood / AirDash hero strip: campus flood score + PCD AQI + FloodDash BKK
         The single slim row below the topbar — the operator's first read of
         the dashboard is the campus resilience posture, not the world clock
         wall. Each cell is click-to-fly-to for the map. ── */}
      <FloodAirHeroStrip
        floodWatch={floodWatchMerged}
        floodLoading={floodWatch.fallbackTier === "loading"}
        floodAgeMinutes={floodWatch.ageMinutes}
        floodFallbackTier={floodWatch.fallbackTier === "loading" ? undefined : floodWatch.fallbackTier}
        airStations={pcdAir.data}
        airAgeMinutes={pcdAir.ageMinutes}
        airFallbackTier={pcdAir.fallbackTier}
        regional={floodRegional.data[0] ?? null}
        regionalAgeMinutes={floodRegional.ageMinutes}
        onFlyTo={flyTo}
      />

      {/* ── Left sidebar: operational or executive brief ── */}
      <aside className={`left-bar ${lens === "executive" ? "left-bar-exec" : ""}`}>
        {lens === "executive" ? (
          <>
            <ExecutiveBrief
              data={executive.data[0] ?? null}
              loading={executive.fallbackTier === "loading"}
              forecast={campusForecast.data[0] ?? null}
              ageMinutes={executive.ageMinutes}
              fallbackTier={executive.fallbackTier === "loading" ? undefined : executive.fallbackTier}
            />
          </>
        ) : lens === "energy" ? (
          <EnergyPanel
            snapshot={energySnapshot}
            forecast={campusForecast.data[0] ?? null}
            fallbackTier="scenario"
            ageMinutes={0}
            onSelectBuilding={handleEnergySelectBuilding}
          />
        ) : lens === "mobility" ? (
          <>
            <TransitBoard />
            <ParkingPanel />
          </>
        ) : lens === "control" ? (
          <>
            <CampusControlPanel />
            <CampusOccupancyPanel />
          </>
        ) : lens === "latkrabang" ? (
          <>
            <LatKrabangPanel />
            <DistrictInsightsPanel />
            <CampusOccupancyPanel />
          </>
        ) : (
          <>
            {/* Air quality — first so staff see PM2.5 status immediately */}
            <AqiBadge
              trend={aqiTrend.data[0] ?? null}
              loading={aqiTrend.fallbackTier === "loading"}
              ageMinutes={aqiTrend.ageMinutes}
              fallbackTier={aqiTrend.fallbackTier === "loading" ? undefined : aqiTrend.fallbackTier}
            />
            {/* Live HII ThaiWater + FloodDash watch score — OPS / ENV / SAF / VIB / AMN.
               PCD air + FloodDash BKK score live in FloodAirHeroStrip (top chrome). */}
            <FloodWatchPanel
              data={floodWatchMerged}
              loading={floodWatch.fallbackTier === "loading"}
              ageMinutes={floodWatch.ageMinutes}
              fallbackTier={floodWatch.fallbackTier === "loading" ? undefined : floodWatch.fallbackTier}
              regional={floodRegional.data[0] ?? null}
              onFlyTo={flyTo}
            />
            {/* Watershed diagram — khlongs, canals, drains grouped by
               sub-catchment, with the inline flow-direction schematic.
               Pairs with the on-map `waterways` + `waterways-flow` layers. */}
            <WaterWatershedPanel onFlyTo={flyTo} />

            {/* Per-area flood level — what is actually flooding where, and how
                we know. Reads FloodDash's published tier/score/scope rather
                than deriving its own severity. */}
            <FloodAreaBoard
              feed={floodAreaFeed.data[0] ?? null}
              loading={floodAreaFeed.fallbackTier === "loading"}
              ageMinutes={floodAreaFeed.ageMinutes}
              fallbackTier={floodAreaFeed.fallbackTier === "loading" ? undefined : floodAreaFeed.fallbackTier}
            />
            {/* Where the water actually was — Copernicus GFM observed extent from
                Sentinel-1. Area is counted off the raster; no severity band is
                invented, and an empty scan reports an empty scan. */}
            <GfmExtentPanel
              feed={gfmFlood.data[0] ?? null}
              loading={gfmFlood.fallbackTier === "loading"}
              ageMinutes={gfmFlood.ageMinutes}
              fallbackTier={gfmFlood.fallbackTier === "loading" ? undefined : gfmFlood.fallbackTier}
            />
            {/* Campus occupancy — venues, canteens, labs, library */}
            <CampusOccupancyPanel />
            {/* City Reports — populated via LINE / Telegram */}
            <CityReportsDashboard
              reports={cityReports.data}
              loading={cityReports.fallbackTier === "loading"}
              onFlyTo={handleCityFlyTo}
              onSelect={handleSelectIncident}
              ageMinutes={cityReports.ageMinutes}
              fallbackTier={cityReports.fallbackTier === "loading" ? undefined : cityReports.fallbackTier}
            />
            {/* Campus brief — KMITL transit feeders + dorm-belt occupancy */}
            <CampusBrief
              hour={hour}
              isWeekend={isWeekend}
              iticEvents={iticEvents.data}
              cityReports={cityReports.data}
              trafficSampleCount={trafficSamples.length}
              fallbackTier={
                iticEvents.fallbackTier === "loading" ? undefined :
                cityReports.fallbackTier === "loading" ? undefined :
                (iticEvents.fallbackTier ?? cityReports.fallbackTier)
              }
              ageMinutes={Math.min(iticEvents.ageMinutes ?? 999, cityReports.ageMinutes ?? 999)}
            />
            <div className="left-section">
              <DeviceCheckIn presence={presence} onRequest={requestDevice} onClear={clearDevice} />
            </div>
          </>
        )}
      </aside>

      {/* ── Map center — nothing overlaps this ── */}
      <main className="map-area">
        <div className="map-host">
          <DeckGL
            viewState={viewState}
            onViewStateChange={onViewStateChangeBatched}
            controller={{ dragRotate: true, touchRotate: true, inertia: true }}
            layers={allLayers}
            getTooltip={tooltipForPickMemo}
            onClick={handleMapClick}
            onHover={(info) => {
              if (info.coordinate) {
                const now = performance.now();
                if (now - cursorLastCommitMs.current < 200) return; // ≤5 Hz — readout only needs to feel live
                cursorLastCommitMs.current = now;
                setCursorCoord([info.coordinate[0], info.coordinate[1]]);
              } else if (cursorCoord) {
                setCursorCoord(null);
              }
            }}
          >
            <MapLibreMap
              mapStyle={theme === "light" ? BASEMAP_LIGHT : theme === "high-contrast" ? BASEMAP_HIGH_CONTRAST : BASEMAP_DARK}
              mapLib={maplibregl as unknown as typeof maplibregl}
              attributionControl={false}
              renderWorldCopies={false}
              // Lock the camera to Lat Krabang district bbox. MapLibre clamps
              // the basemap; deck.gl is clamped separately in onViewStateChange.
              maxBounds={LAT_KRABANG_BOUNDS}
              minZoom={11}
            >
              {/* Google Map Tiles API rasters — minted session token kept client-side.
                  Mounted below the basemap labels so labels stay legible over imagery. */}
              {enabledLayers.has("google-satellite") && googleSatSession && (
                <Source
                  id="google-satellite-src"
                  type="raster"
                  tiles={[googleTileTemplate(googleSatSession)]}
                  tileSize={256}
                  maxzoom={20}
                >
                  <MapLayer
                    id="google-satellite-lyr"
                    type="raster"
                    paint={{ "raster-opacity": 1 }}
                    beforeId="labels-top"
                  />
                </Source>
              )}
              {enabledLayers.has("google-traffic") && googleTrafficSession && (
                <Source
                  id="google-traffic-src"
                  type="raster"
                  tiles={[googleTileTemplate(googleTrafficSession)]}
                  tileSize={256}
                  maxzoom={20}
                >
                  <MapLayer
                    id="google-traffic-lyr"
                    type="raster"
                    paint={{ "raster-opacity": 0.85 }}
                    beforeId="labels-top"
                  />
                </Source>
              )}
            </MapLibreMap>
          </DeckGL>
          <BuildingSearch
            buildings={buildings}
            onSelect={handleBuildingSearchSelect}
          />
          {/* Map zoom controls — back-up for gesture / trackpad */}
          <div className="zoom-controls" aria-label="Map zoom controls">
            <button onClick={() => zoomBy(0.6)} aria-label="Zoom in" className="zoom-btn">+</button>
            <button onClick={() => zoomBy(-0.6)} aria-label="Zoom out" className="zoom-btn">−</button>
          </div>
          {isSubstructure && <SubstructureLegend />}
          {/* Focus badge — tells the user the camera is pinned to the
              Lat Krabang district bbox (out-of-reach of full-Bangkok pan).
              Positioned above the zoom controls so it's glanceable but
              doesn't fight the compass. */}
          <div
            className="focus-badge"
            aria-label="Map focus locked to Lat Krabang district"
            title="Camera is locked to Lat Krabang district bbox. Use the LATKRABANG lens for the canonical view."
          >
            <span className="focus-badge-dot" />
            <span className="focus-badge-text">FOCUS · LAT KRABANG</span>
          </div>
          {/* Compass — needle stays glued to TRUE north as the camera rotates.
              Click to snap back to north-up (bearing 0). Tooltip shows the
              current bearing so it's also a heading readout. */}
          <button
            type="button"
            className="map-compass"
            onClick={() => {
              programmaticTransitionUntil.current = Date.now() + 600;
              setViewState((v) => ({ ...v, bearing: 0, transitionDuration: 400 }));
            }}
            title={`Bearing ${Math.round(((viewState.bearing % 360) + 360) % 360)}° — click to face north`}
            aria-label={`Bearing ${Math.round(((viewState.bearing % 360) + 360) % 360)} degrees. Click to reset to north-up.`}
          >
            <svg
              viewBox="0 0 40 40"
              style={{ transform: `rotate(${-viewState.bearing}deg)` }}
              aria-hidden
            >
              <circle cx="20" cy="20" r="17" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.55" />
              {/* North half — accent / pink */}
              <polygon points="20,4 14,20 20,17 26,20" fill="var(--brand)" />
              {/* South half — muted */}
              <polygon points="20,36 14,20 20,23 26,20" fill="currentColor" opacity="0.55" />
              <text x="20" y="13" textAnchor="middle" fontSize="6" fontWeight="700" fill="var(--brand)" fontFamily="var(--font-mono)">N</text>
            </svg>
          </button>
          {cursorCoord && (
            <div className="map-coord mono" aria-live="off" role="status">
              <span className="map-coord-label">LAT</span>
              <span className="map-coord-val">{cursorCoord[1].toFixed(5)}</span>
              <span className="map-coord-label">LNG</span>
              <span className="map-coord-val">{cursorCoord[0].toFixed(5)}</span>
              <span className="map-coord-label">Z</span>
              <span className="map-coord-val">{viewState.zoom.toFixed(1)}</span>
            </div>
          )}

          {/* Waterway legend — appears when the waterways layer is on.
              Sits next to the lat/lng readout at the bottom-left of the map.
              Hides itself once the user closes the waterways layer. */}
          <WaterwayLegend visible={enabledLayers.has("waterways")} />
          <ImageryStatus enabledLayers={enabledLayers} sessionStartedAt={sessionStartedAt} />
          <BuildingCard
            building={selectedBuilding}
            coord={selectedCoord}
            onClose={handleCloseBuilding}
          />
          <GenericFeatureCard
            feature={selectedFeature}
            onClose={handleCloseFeature}
          />
          <IncidentCard
            incident={cityReports.data.find((r) => r.id === selectedIncidentId) ?? null}
            onClose={handleCloseIncident}
            apiBase={API_BASE}
            onReplied={cityReports.refetch}
          />
        </div>
        {/* The concierge launcher belongs to the map, not the viewport: docked
            to the map's own top-right corner it cannot land on top of the
            left rail's gauge panel the way a viewport-fixed banner did. */}
        <ChatBox apiBase={API_BASE} />
      </main>

      {/* ── Right sidebar: news (scrollable) + layer controls ── */}
      <aside className="right-bar">
        {lens === "executive" && executive.data[0] && (
          <div className="right-alerts">
            <StrategicAlerts
              alerts={executive.data[0].alerts}
              ageMinutes={executive.ageMinutes}
              fallbackTier={executive.fallbackTier === "loading" ? undefined : executive.fallbackTier}
            />
          </div>
        )}
        {(lens === "environment" || lens === "executive") && campusForecast.data[0] && (
          <div className="right-forecast">
            <div className="forecast-panel">
              <header className="forecast-panel-head">
                <span className="lcars-bar" />
                <span className="eyebrow mono">TimeFM · AQI 24h</span>
                <span className="mono caption" style={{
                  color: campusForecast.data[0].aqi.model === "timefm" ? "var(--data)" : "var(--text-3)",
                }}>
                  {campusForecast.data[0].aqi.model === "timefm" ? "VERTEX AI" : campusForecast.data[0].aqi.model.toUpperCase()}
                </span>
              </header>
              <ForecastSparkline series={campusForecast.data[0].aqi} height={52} showLabels />
            </div>
          </div>
        )}
        <div className="right-trends">
          <TrendsPanel
            snapshots={trends.data}
            loading={trends.fallbackTier === "loading"}
            ageMinutes={trends.ageMinutes}
            onRefresh={trends.refetch}
            tier={trends.fallbackTier}
          />
        </div>
        {lens === "executive" && executive.data[0] && (
          <div className="right-peers">
            <PeerComparison
              peers={executive.data[0].peers}
              ageMinutes={executive.ageMinutes}
              fallbackTier={executive.fallbackTier === "loading" ? undefined : executive.fallbackTier}
            />
          </div>
        )}
        <div className="right-gistda">
          <GistdaPulse apiBase={API_BASE} />
        </div>
        <div className="right-news">
          <NewsDesk
            items={news.data}
            loading={news.fallbackTier === "loading"}
            ageMinutes={news.ageMinutes}
            fallbackTier={news.fallbackTier === "loading" ? undefined : news.fallbackTier}
            onRefresh={news.refetch}
            digestToplines={newsDigest.toplines}
            digestTotal={newsDigest.totalInWindow}
          />
        </div>
        <div className="right-layers">
          <LayerPalette
            lens={lens}
            onLensChange={onLensChange}
            enabled={enabledLayers}
            onToggleLayer={onToggleLayer}
            onClearAllLayers={onClearAllLayers}
            baseMap={baseMap}
            onBaseMapChange={setBaseMap}
            zoneMetric={zoneMetric}
            onZoneMetricChange={setZoneMetric}
          />
        </div>
      </aside>

      {/* ── Bottom ticker rail — moved here from the top chrome so the
         operator's first read of the dashboard is the map, not a ticker
         wall. Two 22-px rows; first markets, then news. ── */}
      <div className="shell-markets">
        <MarketsTicker
          snapshot={markets.data[0] ?? null}
          loading={markets.fallbackTier === "loading"}
          tier={markets.fallbackTier}
        />
      </div>
      <div className="shell-ticker">
        <NewsTicker items={news.data} loading={news.fallbackTier === "loading"} />
      </div>

      <SourceCatalog open={catalogOpen} onClose={() => setCatalogOpen(false)} adapterHealth={adapterHealth} />
      <AnalyticsBoard open={analyticsOpen} onClose={() => setAnalyticsOpen(false)} />
      <Manual open={manualOpen} onClose={() => setManualOpen(false)} />
      <StandardsPanel open={standardsOpen} onClose={() => setStandardsOpen(false)} />
      <AboutModal
        open={aboutOpen}
        onClose={() => {
          setAboutOpen(false);
          try { window.localStorage.setItem("kmitl:about-v1", "1"); } catch {}
        }}
      />
      <SheetsPanel
        open={sheetsOpen}
        onClose={() => { setSheetsOpen(false); setSheetsConfigured(Boolean(loadSheetsUrl())); }}
      />
      <CctvStreamModal
        camera={cctvModalCamera}
        onClose={() => setCctvModalCamera(null)}
        apiBase={API_BASE}
      />
      {isMobile && <MobileNav panel={mobilePanel} onChange={setMobilePanel} />}
    </div>
  );
}
